import { DsdFile } from "@/types/dsd";
import { DeletedUserAvatar } from "@/components/common/deleted-user-avatar";
import {
  PeriodChip,
  ReportPeriod,
  ReportType,
  TypeChip,
  YearChip,
} from "@/components/common/report-chip";
import { useUserNames } from "@/hooks/queries/use-user-names";
import { cn } from "@/lib/utils";
import { Avatar } from "@/toggle-ui-kit/avatar";
import { getFormattedTimeWithDate } from "@/utils/time";
import {
  getUserDisplayName,
  isDeletedUser,
} from "@/utils/user-display-name";
import { ReactNode } from "react";

export interface DsdInfoProps {
  data: DsdFile;
  selected?: boolean;
  disabled?: boolean;
  onClick?: () => void;
  className?: string;
  /** 카드 하단(구분선 아래)에 추가로 렌더할 내용. */
  footer?: ReactNode;
  /** 여백을 줄이고 업로드 정보를 한 줄로 압축한 컴팩트 레이아웃. */
  compact?: boolean;
}

export const DsdInfo = ({
  data,
  selected = false,
  disabled = false,
  onClick,
  className,
  footer,
  compact = false,
}: DsdInfoProps) => {
  const { data: userNamesMap, isLoading: isLoadingUserName } = useUserNames([
    data.creator,
  ]);

  // DsdFile -> 표시 데이터로 변환
  const year = data.reportYear ?? "-";
  const period = data.reportQuarter as ReportPeriod;
  const reportType = data.financialStatementType as ReportType;
  const fileName = data.displayName ?? data.name;
  const uploader = getUserDisplayName(userNamesMap, data.creator);
  const isDeletedUploader = isDeletedUser(userNamesMap, data.creator);
  const uploadDate = getFormattedTimeWithDate(data.createdAt);

  const uploaderAvatar = !isLoadingUserName && (
    <>
      {isDeletedUploader ? (
        <DeletedUserAvatar size="16" />
      ) : (
        <Avatar email={data.creator} displayName={uploader} size="16" />
      )}
    </>
  );

  return (
    <div
      onClick={disabled ? undefined : onClick}
      className={cn(
        "flex flex-col w-full rounded-md border bg-white",
        compact ? "gap-2.5 p-3.5" : "gap-4 overflow-hidden p-5 transition-all",
        !disabled &&
          !selected &&
          onClick &&
          "hover:bg-neutral-50 hover:cursor-pointer",
        disabled && "opacity-50 cursor-not-allowed",
        className,
      )}
    >
      {/* 상단 영역: 칩들 + 파일명 */}
      <div className={cn("flex flex-col", compact ? "gap-1" : "gap-1.5")}>
        {/* 칩 그룹 */}
        <div className="flex gap-1 items-center">
          {reportType && <TypeChip type={reportType} size="small" />}
          <YearChip year={year} size="small" />
          {period && <PeriodChip period={period} size="small" />}
        </div>
        {/* 파일명 */}
        <p className="body-2-normal-medium text-label-normal truncate w-full">
          {fileName}
        </p>
      </div>

      {/* 구분선 */}
      <div className="border-t border-line-normal" />

      {/* 하단 영역: 업로드 정보 */}
      {compact ? (
        <div className="flex flex-wrap items-center gap-x-1.5 gap-y-1 caption-2-regular text-label-alternative">
          {uploaderAvatar}
          <span className="text-label-strong">
            {isLoadingUserName ? "..." : uploader}
          </span>
          <span aria-hidden>·</span>
          <span>{uploadDate}</span>
        </div>
      ) : (
        <div className="flex gap-3">
          <div className="flex flex-col gap-2 shrink-0">
            <span className="label-2-medium text-label-alternative">
              업로드한 사람
            </span>
            <span className="label-2-medium text-label-alternative">
              업로드 일시
            </span>
          </div>
          <div className="flex flex-col gap-2">
            <div className="flex items-center gap-1">
              {uploaderAvatar}
              <span className="label-2-medium text-label-strong">
                {isLoadingUserName ? "..." : uploader}
              </span>
            </div>
            <span className="label-2-medium text-label-normal">
              {uploadDate}
            </span>
          </div>
        </div>
      )}

      {footer && (
        <>
          <div className="border-t border-line-normal" />
          {footer}
        </>
      )}
    </div>
  );
};
